import { useEffect, useMemo, useState } from "react";
import axios from "axios";

export type TransportLocationDto = {
    id: number;
    eventId: number;
    nameDK: string;
    nameEN?: string | null;
    locationType: "pickup" | "dropoff" | "both" | string;
};

function normType(t?: string) {
    return (t ?? "").toLowerCase();
}

export function useTransportLocations(eventId?: string, lang: "dk" | "en" = "dk") {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [allLocations, setAllLocations] = useState<TransportLocationDto[]>([]);

    useEffect(() => {
        if (!eventId) return;

        const eid = Number(eventId);
        if (!Number.isFinite(eid) || eid <= 0) {
            setError("Invalid eventId.");
            return;
        }

        (async () => {
            try {
                setLoading(true);
                setError(null);

                const apiUrl = process.env.REACT_APP_API_URL;

                // ✅ Your real endpoint returns an ARRAY
                const res = await axios.get<TransportLocationDto[]>(
                    `${apiUrl}/api/transport/admin/locations`,
                    { params: { eventId: eid } }
                );

                setAllLocations(Array.isArray(res.data) ? res.data : []);
            } catch (e: any) {
                setError(e?.response?.data ?? "Could not load transport locations.");
                setAllLocations([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [eventId]);

    const pickupLocations = useMemo(() => {
        return allLocations.filter((l) => {
            const t = normType(l.locationType);
            return t === "pickup" || t === "both";
        });
    }, [allLocations]);

    const dropoffLocations = useMemo(() => {
        return allLocations.filter((l) => {
            const t = normType(l.locationType);
            return t === "dropoff" || t === "both";
        });
    }, [allLocations]);

    const pickupSuggestions = useMemo(() => {
        return pickupLocations
            .map((l) => (lang === "en" ? (l.nameEN?.trim() || l.nameDK) : l.nameDK).trim())
            .filter(Boolean);
    }, [pickupLocations, lang]);

    const dropoffSuggestions = useMemo(() => {
        return dropoffLocations
            .map((l) => (lang === "en" ? (l.nameEN?.trim() || l.nameDK) : l.nameDK).trim())
            .filter(Boolean);
    }, [dropoffLocations, lang]);

    return {
        loading,
        error,

        allLocations,
        pickupLocations,
        dropoffLocations,

        pickupSuggestions,
        dropoffSuggestions,
    };
}